import {
  Button,
  createStyles,
  Divider,
  Icon,
  makeStyles,
  Theme,
  Typography
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import clsx from 'clsx';
import { ArrowTooltip } from ".";
import { Scrollbars } from 'react-custom-scrollbars';
import { useTranslation } from "react-i18next";
import { nanoid } from "@reduxjs/toolkit";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    verticalDivider: {
      padding: "0px 2px 0px 2px"
    },
    icons: {
      '& > .fa': {
        margin: theme.spacing(2),
      },
      display: "flex",
      padding: theme.spacing(0.5),
    },
    iconButton: {
      minWidth: "0px !important",
      flex: 1
    },
    iconSize: {
      fontSize: "1.0em",
      width: "auto"
    },
    success: {
      color: theme.palette.success.main
    },
    error: {
      color: theme.palette.error.main
    },
    disabled: {
      opacity: 0.3
    },
    listIcon: {
      color: "rgba(0, 0, 0, 0.54)",
      fontSize: "20px"
    }
  })
);

export const DenseDivider = (props) => {
  const {spacing, ...other} = props;

  return (
    <Divider {...other} style={{
      marginTop: `${spacing}px`,
      marginBottom: `${spacing}px`
    }}/>
  );
};

export const IconToolBar = (props) => {
  const classes = useStyles();
  const {children, margin} = props;

  return (
    <div className={classes.icons} style={{
      marginLeft: `${margin}em`,
      marginRight: `${margin}em`
    }}>
      {children}
    </div>
  );
};

export const ToolBarDivider = () => {
  const classes = useStyles();

  return (
    <Typography className={classes.verticalDivider}>{"|"}</Typography>
  );
};

export const FontAwesomeIcon = (props) => {
  const classes = useStyles();
  const {icon, color, success, error, disabled, listIcon} = props;
  const errorColor = error ? classes.error : "";

  return (
    <Icon
      className={clsx(`fa fa-sm ${icon}`, classes.iconSize, {
        [classes.disabled]: disabled,
        [classes.listIcon]: listIcon
      })}
      color={color ? color : "primary"}
      classes={{
        colorPrimary: success ? classes.success : errorColor
      }}
    />
  );
};

export const ToolBarButton = (props) => {
  const {icon, visible, title, onClick, color, success,
    error, disabled, ...other} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const isVisible = visible !== undefined ? visible : true;
  const tooltipText = disabled ? `${title} (${t('toolbar.button-disabled')})` : title;

  return (
    <React.Fragment>
      {isVisible && (<ArrowTooltip title={tooltipText}>
        <Button
          size="small"
          className={classes.iconButton}
          onClick={disabled ? undefined :onClick}
          {...other}
        >
          <FontAwesomeIcon
            icon={icon}
            error={error}
            color={color}
            success={success}
            disabled={disabled}
          />
        </Button>
      </ArrowTooltip>)}
    </React.Fragment>
  );
};

export const SlimScrollbar = (props) => {
  const {scrollHeight, children} = props;

  return (
    <React.Fragment>
      {/** Use scroll height that was passed in */}
      {scrollHeight && (<Scrollbars
        autoHide
        autoHeight
        autoHeightMin={scrollHeight}
        autoHeightMax={scrollHeight}
        autoHideTimeout={250}
        autoHideDuration={100}
      >
        {children}
      </Scrollbars>)}

      {/** No scroll height was provided */}
      {!scrollHeight && (<Scrollbars
        autoHide
        autoHideTimeout={250}
        autoHideDuration={100}
      >
        {children}
      </Scrollbars>)}
    </React.Fragment>
  );
};

export const DynamicScrollbar = (props) => {
  const {scrollHeight, scrollPercent, children} = props;

  const [scroll, setScroll] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);
  const listElementID = useState(nanoid())[0];

  const getMaxHeight = () => {
    const percentHeight = (scrollPercent/100) * window.innerHeight;
    return Math.min(scrollHeight, percentHeight);
  };

  const checkScrollHeight = () => {
    let element = document.getElementById(listElementID);
    if (!element) return;

    const height = element.offsetHeight;
    const maxHeight = getMaxHeight();

    setScroll(height > maxHeight);
    setMaxHeight(maxHeight);
  };

  useEffect(() => {
    checkScrollHeight();
    window.addEventListener('resize', checkScrollHeight);

    return () => {
      window.removeEventListener('resize', checkScrollHeight);
    };
  }, []);

  useEffect(() => {
    checkScrollHeight();
  }, [children, window.innerHeight]);

  return (
    <React.Fragment>
      {(!scroll) && (
        <div id={listElementID}>
          {children}
        </div>
      )}

      {(scroll) && (
        <SlimScrollbar scrollHeight={maxHeight}>
          <div id={listElementID}>
            {children}
          </div>
        </SlimScrollbar>
      )}
    </React.Fragment>
  );
};